import React from 'react';
import { SquarePen, PauseCircle, Archive, PlayCircle, MoreVertical } from 'lucide-react';
import { Link, router } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import { PageHeader, DataTable, Badge, Button, Dropdown } from '../../../design-system/components';
import { formatCurrency } from '../../../lib/formatters';
import {
  admin_campaign_path,
  new_admin_campaign_path,
  edit_admin_campaign_path,
  pause_admin_campaign_path,
  archive_admin_campaign_path,
  activate_admin_campaign_path,
} from '../../../lib/routes';
const statusVariant = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
};
function commissionDisplay(campaign) {
  if (campaign.commission_type === 'percentage') {
    return `${parseFloat(campaign.commission_rate)}%`;
  }
  return formatCurrency(campaign.commission_rate * 100);
}
function durationDisplay(campaign) {
  switch (campaign.commission_duration_type) {
    case 'one_time':
      return 'One-time';
    case 'recurring':
      return 'Recurring';
    case 'recurring_capped':
      return `${campaign.commission_duration_months} months`;
    default:
      return campaign.commission_duration_type;
  }
}
function getActions(campaign) {
  const items = [
    {
      label: 'Edit',
      onClick: () => router.visit(edit_admin_campaign_path(campaign.id)),
      icon: <SquarePen size={16} />,
    },
  ];
  if (campaign.status === 'active') {
    items.push({
      type: 'divider',
    });
    items.push({
      label: 'Pause',
      onClick: () => router.post(pause_admin_campaign_path(campaign.id)),
      icon: <PauseCircle size={16} />,
    });
    items.push({
      label: 'Archive',
      onClick: () => router.post(archive_admin_campaign_path(campaign.id)),
      destructive: true,
      icon: <Archive size={16} />,
    });
  } else if (campaign.status === 'paused') {
    items.push({
      type: 'divider',
    });
    items.push({
      label: 'Activate',
      onClick: () => router.post(activate_admin_campaign_path(campaign.id)),
      icon: <PlayCircle size={16} />,
    });
    items.push({
      label: 'Archive',
      onClick: () => router.post(archive_admin_campaign_path(campaign.id)),
      destructive: true,
      icon: <Archive size={16} />,
    });
  } else if (campaign.status === 'archived') {
    items.push({
      type: 'divider',
    });
    items.push({
      label: 'Activate',
      onClick: () => router.post(activate_admin_campaign_path(campaign.id)),
      icon: <PlayCircle size={16} />,
    });
  }
  return items;
}
const columns = [
  {
    id: 'name',
    header: 'Name',
    sortable: true,
    render: (row) => (
      <div className="flex items-center gap-2">
        <Link
          href={admin_campaign_path(row.id)}
          className="font-medium text-fg hover:text-fg-secondary"
        >
          {row.name}
        </Link>
        {row.is_default && (
          <Badge variant="info" size="sm">
            Default
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'commission_type',
    header: 'Type',
    render: (row) => <span className="capitalize">{row.commission_type}</span>,
  },
  {
    id: 'commission_rate',
    header: 'Rate',
    render: (row) => commissionDisplay(row),
  },
  {
    id: 'duration',
    header: 'Duration',
    render: (row) => durationDisplay(row),
  },
  {
    id: 'affiliate_count',
    header: 'Affiliates',
    sortable: true,
    render: (row) => row.affiliate_count,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => (
      <Badge variant={statusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    render: (row) => (
      <Dropdown
        trigger={
          <button className="rounded p-1 text-fg-tertiary hover:bg-surface-muted hover:text-fg-secondary">
            <MoreVertical size={20} />
          </button>
        }
        items={getActions(row)}
      />
    ),
  },
];
export default function Index({ campaigns }) {
  return (
    <AdminLayout>
      <PageHeader
        title="Campaigns"
        description="Manage your affiliate campaigns"
        actions={
          <Link href={new_admin_campaign_path()}>
            <Button>New Campaign</Button>
          </Link>
        }
      />

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={campaigns}
          keyAccessor="id"
          emptyTitle="No campaigns yet"
          emptyDescription="Create your first campaign to start onboarding affiliates."
          emptyAction={
            <Link href={new_admin_campaign_path()}>
              <Button>Create Campaign</Button>
            </Link>
          }
        />
      </div>
    </AdminLayout>
  );
}
